/**
 * C2 — "Changes to your sharing", newest first.
 *
 * Renamed from SeatChangeLog with Story #5648 build v2, and re-worded with it:
 * every event now reads as a change to SHARING, because that is what each one
 * is. "The sponsor offered a sponsored seat" described a licensing event that
 * does not exist in this product's vocabulary; "Meridian Ventures invited you to
 * its portfolio" describes the thing that actually happened.
 *
 * Sourced from the deployed GET /innovateflow/sponsorship/seat-changes — the
 * caller-scoped audit projection {event_id, event, actor, organisation, at}.
 * The sentences live in sharingModel.describeChange so the vocabulary is in one
 * place and can be tested without rendering anything.
 *
 * The subtitle is a promise, and it is why the wording here matters: "every
 * invitation, grant and revocation appears here, with the same words as the
 * email you were sent". A feed that describes the same event differently from
 * the notification is a feed the reader cannot reconcile.
 */
import { CHANGE_EMPHASIS, collapseChanges, dayAndMonth, describeChange, spanLabel, timeOfDay } from './sharingModel'
import type { ChangeEntry } from './sharingModel'
import type { SeatChange } from '../../types'

export interface SharingChangesProps {
  changes: SeatChange[]
  loaded: boolean
  /** Distinguishes "loaded, nothing to show" from "the read failed". */
  error?: boolean
}

/** Renders one sentence with the figure's bold word emphasised in place. */
function Sentence({ entry }: { entry: ChangeEntry }) {
  const change = entry.change
  // A churn line carries its own sentence (see collapseChurn) — it says a
  // thing no single event's sentence can say.
  const text = entry.pairText ?? describeChange(change)
  const emphasis = entry.pairText ? entry.pairEmphasis : CHANGE_EMPHASIS[change.event]
  const at = emphasis ? text.indexOf(emphasis) : -1
  if (at < 0 || !emphasis) return <>{text}</>
  return (
    <>
      {text.slice(0, at)}
      <b className="text-slate-900">{emphasis}</b>
      {text.slice(at + emphasis.length)}
    </>
  )
}

export default function SharingChanges({ changes, loaded, error }: SharingChangesProps) {
  // Newest first, and an unbroken run of the same sentence collapsed into one
  // line that says how many times and over what span — see collapseChanges for
  // the measurement that made this necessary.
  const ordered = collapseChanges(changes)

  return (
    // Heading inside the part's root, in every state. "Changes to your sharing"
    // with an honest "nothing yet" underneath is information; a missing section
    // is not, and the reader cannot tell it from a screen that failed to load.
    <section data-part="SharingChanges" aria-label="Changes to your sharing" className="mb-5">
      <h2 className="mb-2 flex flex-wrap items-baseline gap-2 text-[14px] font-bold text-slate-900">
        Changes to your sharing
        <span className="text-[11.5px] font-medium text-slate-500">
          · every invitation, grant and revocation appears here, with the same
          words as the email you were sent
        </span>
      </h2>
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-[12.5px] leading-relaxed text-slate-600">
        {!loaded && !error && <p className="text-slate-400">Loading your sharing history…</p>}
        {error && (
          <p className="text-rose-600">
            Could not load your sharing history — this list is not a statement
            about your sharing until it loads.
          </p>
        )}
        {loaded && !error && ordered.length === 0 && (
          <p>Nothing has changed about your sharing yet.</p>
        )}
        {loaded && !error && ordered.length > 0 && (
          <ul>
            {ordered.map((entry) => (
              <li key={entry.change.event_id}>
                {dayAndMonth(entry.at)} — <Sentence entry={entry} />
                {entry.pairText ? (
                  <span className="text-slate-400">
                    {' · '}{spanLabel(entry.firstAt, entry.at)}
                  </span>
                ) : entry.count > 1 && (
                  <span className="text-slate-400">
                    {' · '}{entry.count} times
                    {timeOfDay(entry.firstAt) && timeOfDay(entry.at)
                      ? `, ${timeOfDay(entry.firstAt)}–${timeOfDay(entry.at)}`
                      : ''}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
